'use client';

import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { isEncryptedCardMask } from '@sola/shared-utils/checkout/credit-cards.js';
import { SENSITIVE_MASK_CHAR } from '@sola/shared-utils/constants.js';

import { InputField, InputFieldProps } from '../input-field/input-field';
import { resetCCNumberAndCode, useCreditCardFields } from './utils';

export type CardCodeFieldProps = Omit<
  InputFieldProps,
  | 'maskOptions'
  | 'placeholder'
  | 'prefix'
  | 'type'
  | 'minLength'
  | 'maxLength'
  | 'pattern'
>;

export const CardCodeField: React.FC<CardCodeFieldProps> = ({
  label,
  help,
  ...fieldProps
}) => {
  const { card, validator } = useCreditCardFields();
  const formContext = useFormContext();

  const { t } = useTranslation();

  const clearValue = () => {
    resetCCNumberAndCode(formContext);
  };

  return (
    <InputField
      autoComplete="cc-csc"
      inputMode="numeric"
      label={label || t('checkout.form.billingFields.securityCodeLabel')}
      help={help || t('checkout.form.card.codeTooltip')}
      minLength={{
        value: card?.code?.size || 3,
        message: t('checkout.form.card.codeInvalid'),
      }}
      // @ts-expect-error: maxLength is getting overridden in the type signature, but we want to provide an error if the value is not valid
      maxLength={{
        value: card?.code?.size || 3,
        message: t('checkout.form.card.codeInvalid'),
      }}
      validate={(value) => {
        if (isEncryptedCardMask(value)) {
          return true;
        }
        return validator.cvv(value, card?.code?.size || 3).isValid;
      }}
      maskOptions={{ mask: [...new Array(card?.code.size || 3).fill(/\d/)] }}
      onFocus={card?.hasEncryptedMask ? clearValue : undefined}
      value={
        card?.hasEncryptedMask
          ? Array(card?.code?.size ?? 3)
              .fill(SENSITIVE_MASK_CHAR)
              .join('')
          : fieldProps.value
      }
      {...fieldProps}
    />
  );
};
